import {
    Button,
    Card,
    CardList,
    DarkModeToggle,
    documentObject,
    FIDescription,
    Flexer,
    FOLD_DRAG_LOCK,
    Header,
    Heading,
    Icon,
    If,
    Image,
    Link,
    ModalClose,
    Pill,
    plural,
    Progress,
    Sidebar,
    Stack,
    stopEvent,
    Text,
    Timeline,
    TimelineItem,
    useDrag,
    useStorage,
    View,
} from '@fold-dev/core'
import { useEffect, useMemo, useState } from 'react'
import { TbArrowUp, TbFilters, TbHeart } from 'react-icons/tb'
import { useNavigate, useParams } from 'react-router'
import {
    Block,
    Error,
    getStartEndRepeat,
    ProjectUserPreview,
    Tasks,
    TeamUserPreview,
    useDateButton,
} from '../components'
import { byLikes, byPosition, logger, useDocumentTitle } from '../helpers'
import { useProjects, useTaskLikes, useTasks } from '../hooks/api.hooks'
import { LogoNew } from '../logo'
import { FRONTEER_JWT, FRONTEER_JWT_TEMP, useAuth } from '../hooks/auth.hook'
import { useAppStore } from '../store/app'
import { useSubscription } from '../hooks/websocket.hook'

const Disclaimer = () => {
    return (
        <Text
            textAlign="center"
            colorToken="indigo-50">
            By using Fronteer, you agree to our{' '}
            <a
                href="https://fronteer.app/terms-of-use"
                target="_blank"
                style={{ color: 'currentColor' }}>
                terms of use
            </a>{' '}
            and{' '}
            <a
                href="https://fronteer.app/license"
                target="_blank"
                style={{ color: 'currentColor' }}>
                license agreement
            </a>
            .
        </Text>
    )
}

const TaskItem = ({ task, onClick, active }) => {
    const { start, end, repeat } = useMemo(() => getStartEndRepeat(task.start, task.end, task.repeat), [task])
    const taskLikesAPI = useTaskLikes()
    const { dateLabel, dateColor, dateBackgroundColor, dateIcon } = useDateButton(
        start ? new Date(start) : undefined,
        end ? new Date(end) : undefined,
        repeat,
        task.wholeDay,
        task.complete
    )
    const [liked, setLiked] = useState(!!task.liked)
    const [likes, setLikes] = useState(task.likes)

    const likeTask = async () => {
        try {
            await taskLikesAPI.create(task.id)
            setLiked(true)
            setLikes(+likes + 1)
        } catch (e) {
            logger(e)
        }
    }

    const unlikeTask = async () => {
        try {
            await taskLikesAPI.remove(task.id)
            setLiked(false)
            setLikes(+likes - 1)
        } catch (e) {
            logger(e)
        }
    }

    return (
        <View
            row
            gap="1rem"
            justifyContent="flex-start"
            alignItems="flex-start">
            <View
                row
                colorToken={liked ? 'text-on-color' : 'text'}
                bgToken={liked ? 'surface-inverse' : 'surface-strong'}
                radius={50}
                height={30}
                width={30}
                onClick={() => (liked ? unlikeTask() : likeTask())}
                className="f-buttonize"
                style={{ transform: liked ? 'rotate(180deg)' : undefined }}>
                <Icon
                    icon={TbArrowUp}
                    size="sm"
                />
            </View>
            <View
                column
                className="f-buttonize"
                onClick={() => onClick(task.id)}
                gap="0.3rem"
                p="0.25rem 0 0 0"
                alignItems="flex-start"
                justifyContent="flex-start">
                <Text
                    size="lg"
                    colorToken={task.complete ? 'text-weakest' : 'text'}
                    fontWeight={active ? 900 : 500}
                    textDecoration={task.complete ? 'line-through' : undefined}
                    dangerouslySetInnerHTML={{ __html: task.title }}
                />
                <View
                    row
                    gap="1rem"
                    colorToken="text-weaker">
                    <Text
                        size="sm"
                        color="currentColor">
                        {likes} {plural(likes, 'upvote')}
                    </Text>
                    {!!task.description && (
                        <Icon
                            icon={FIDescription}
                            size="sm"
                        />
                    )}
                    {(task.start || task.end) && (
                        <View
                            row
                            gap="0.5rem"
                            className="f-text sm"
                            color={dateColor}>
                            <Icon
                                icon={dateIcon}
                                size="sm"
                            />
                            {dateLabel}
                        </View>
                    )}
                </View>
            </View>
        </View>
    )
}
export default (props) => {
    const projectsAPI = useProjects()
    const [error, setError] = useState<any>(null)
    const [loading, setLoading] = useState(false)
    const [joined, setJoined] = useState(false)
    const [project, setProject] = useState(null)
    const [tasks, setTasks] = useState([])
    const actions = useAppStore((state) => state.actions)
    const tasksAPI = useTasks()
    const { shortcode } = useParams()
    const [loggedIn, setLoggedIn] = useState(false)
    const { setStorage, getStorage } = useStorage()
    const { currentAuthenticatedUser, parseJwt } = useAuth()
    const [taskId, setTaskId] = useState(null)
    const [completed, setCompleted] = useState(true)
    const task = useMemo(() => tasks.find((t) => t.id == taskId), [taskId, tasks])
    const completedCount = useMemo(() => tasks.filter((t) => t.complete).length, [tasks])

    const fetchData = async () => {
        setLoading(true)
        setError(null)

        try {
            const token = getStorage(FRONTEER_JWT)
            const project: any = await projectsAPI.roadmap({ shortcode })

            setLoggedIn(!!token)

            if (!project) throw Error

            // if there is no token - then use a temp one
            if (!token) {
                setStorage(FRONTEER_JWT, project.token)
                setStorage(FRONTEER_JWT_TEMP, project.token)
            } else {
                // check if the current token has expired
                const { exp } = parseJwt(token)
                const now = new Date().getTime() / 1000

                // if it has - then save the temp one
                if (exp < now) {
                    setStorage(FRONTEER_JWT, project.token)
                    setStorage(FRONTEER_JWT_TEMP, project.token)
                }
            }

            const tasks = await tasksAPI.shortcode({ shortcode })

            setTasks(tasks)
            setProject(project)
            setLoading(false)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    useDocumentTitle(project?.name || 'Fronteer')

    useEffect(() => {
        if (!!shortcode) fetchData()
        documentObject.body.style.backgroundColor = 'var(--f-color-surface)'
    }, [shortcode])

    useEffect(() => {
        window[FOLD_DRAG_LOCK] = true
    })

    useSubscription(project?.id)

    if (error) return <Error error={error} />

    return (
        <View
            column
            bgToken="background"
            width="100%"
            height="100%"
            position="absolute"
            style={{ inset: 0 }}>
            <If if={loading}>
                <Progress
                    value={50}
                    thickness={5}
                    indeterminate
                    bgToken="transparent"
                    position="absolute"
                    variant="success"
                    style={{
                        '--f-progress-radius': 0,
                        'top': 0,
                        'left': 0,
                    }}
                />
            </If>

            <Header
                bg={project?.color || 'var(--f-color-gray-900)'}
                height={50}
                border="none"
                gap="0.5rem"
                p="0 0.5rem 0 1rem"
                style={{
                    '--f-color-surface': project?.color || 'var(--f-color-gray-900)',
                    '--f-color-surface-stronger': 'var(--f-color-surface-strongest)',
                }}>
                {/* <Pill 
                    size="sm"
                    color="#ffffff"
                    outline>
                    {project?.workspace?.name}
                </Pill>
                <Heading
                    as="h5"
                    color="white">
                    {project?.name}
                </Heading> */}
                <LogoNew
                    scale={0.25}
                    color="white"
                />
                <Flexer />
                {!!project?.team?.id && (
                    <TeamUserPreview
                        teamId={project?.team?.id}
                        workspaceId={project?.workspaceId}
                        size="sm"
                    />
                )}
                {!project?.team?.id && (
                    <ProjectUserPreview
                        projectId={project?.id}
                        workspaceId={project?.workspaceId}
                        size="sm"
                    />
                )}
                <Button
                    onClick={(e) => window['fronteer'](e.target)}
                    data-shortcode={shortcode}
                    data-theme="light"
                    height={32}
                    p="0 1rem"
                    colorToken="white"
                    bg="rgba(255,255,255,0.2)"
                    border="none">
                    Submit an issue
                </Button>
                {/*        {project?.joinable && (
                    <Button
                        as="a"
                        target="_blank"
                        href={`${window.location.origin}/c/${shortcode}`}
                        height={32}
                        p="0 1rem"
                        colorToken="white"
                        bg="rgba(255,255,255,0.2)"
                        border="none">
                        View public project
                    </Button>
                )} */}
                <Button
                    as="a"
                    target="_blank"
                    href={window.location.origin}
                    height={35}
                    p="0 1rem"
                    colorToken="white"
                    bg="rgba(255,255,255,0.2)"
                    border="none">
                    {loggedIn ? 'Go to Fronteer ↗' : 'Log in / Sign up'}
                </Button>
            </Header>

            {/*             <Header 
                p="0 0.5rem 0 1rem"
                height={50}>
                <Text>
                    This is a public project and is in read-only mode.
                </Text>
                <Flexer />
            </Header>
 */}

            {/*              */}

            <View
                row
                flex={1}
                gap="var(--frame-padding"
                height="calc(100% - 50px)"
                position="relative"
                bgToken="background"
                alignItems="flex-start"
                p="var(--frame-padding)"
                width="100%"
                style={{
                    maxWidth: taskId ? '100%' : 1300,
                }}>
                <View
                    column
                    gap="1rem"
                    height="fit-content"
                    p="1rem"
                    alignItems="flex-start"
                    alignContent="flex-start"
                    justifyContent="flex-start"
                    width={300}
                    bgToken="surface"
                    border="1px solid var(--f-color-border)"
                    radius="var(--f-radius)"
                    //position="sticky"
                    style={{ top: '2rem', borderRight: '1px solid var(--f-color-border)' }}>
                    {!!project?.roadmapImage && (
                        <Image
                            src={project?.roadmapImage}
                            height={200}
                        />
                    )}
                    <View
                        row
                        width="100%">
                        <Pill outline>{project?.workspace?.name}</Pill>
                        <Flexer />
                        <DarkModeToggle />
                    </View>
                    <Heading as="h1">{project?.name}</Heading>
                    {!!project?.roadmapDescription && (
                        <Text
                            size="sm"
                            colorToken="text-weaker"
                            className="roadmap__description"
                            dangerouslySetInnerHTML={{ __html: project?.roadmapDescription }}
                        />
                    )}
                    {/*   {project?.joinable && (
                        <Button
                            as="a"
                            target="_blank"
                            href={`${window.location.origin}/c/${shortcode}`}
                            size="lg"
                            width="100%"
                            variant="accent">
                            Join Now
                        </Button>
                    )} */}
                    <View
                        row
                        gap="1rem"
                        radius="var(--f-radius)"
                        p="0.75rem 1rem"
                        border="1px solid var(--f-color-indigo-500)">
                        <Text
                            size="sm"
                            colorToken="indigo-500">
                            This public roadmap is powered by Fronteer, currently in Early Access.
                        </Text>
                    </View>
                </View>

                <View
                    border="1px solid var(--f-color-border)"
                    radius="var(--f-radius)"
                    className="f-scrollbar"
                    height="100%"
                    bgToken="surface"
                    position="relative"
                    flex={1}
                    style={{
                        //maxWidth: taskId ? undefined : 1300,
                        overflow: 'scroll',
                    }}>
                    {!!project?.id && (
                        <View
                            column
                            gap="1.5rem"
                            width="100%"
                            m="0 auto"
                            p="3.5rem 3.5rem"
                            style={{}}
                            alignItems="flex-start">
                            <Heading as="h3">Roadmap</Heading>
                            {/*  {!!project?.roadmapDescription && (
                                <View
                                    row
                                    width="100%"
                                    gap="1rem"
                                    radius="var(--f-radius)"
                                    p="1rem"
                                    bgToken="indigo-50"
                                    justifyContent="flex-start">
                                    <Text
                                        p="0"
                                        className="roadmap-intro-text"
                                        dangerouslySetInnerHTML={{ __html: project?.roadmapDescription }}
                                        colorToken="indigo-500">
                                    </Text>
                                </View>
                            )} */}
                            {completedCount != 0 && (
                                <View
                                    row
                                    gap="1rem">
                                    <Text colorToken="text-weak">
                                        {completedCount} {plural(completedCount, 'item')} done
                                    </Text>
                                    <Link
                                        className="f-underline"
                                        href="#"
                                        onClick={(e) => {
                                            stopEvent(e)
                                            setCompleted(!completed)
                                        }}>
                                        {completed ? 'Show all' : 'Hide completed items'}
                                    </Link>
                                </View>
                            )}
                            {tasks
                                .sort(byPosition)
                                .filter((task) => (completed ? !task.complete : true))
                                .map((task) => (
                                    <TaskItem
                                        key={task.id}
                                        task={task}
                                        active={task.id == taskId}
                                        onClick={setTaskId}
                                    />
                                ))}
                        </View>
                    )}
                </View>
                {taskId && (
                    <Sidebar
                        flex={0.75}
                        position="relative"
                        right
                        border="none"
                        bg="transparent"
                        p="3rem"
                        gap="1.5rem"
                        alignItems="stretch"
                        alignContent="stretch"
                        justifyContent="stretch">
                        <ModalClose onClick={() => setTaskId(null)} />

                        <Heading
                            as="h3"
                            dangerouslySetInnerHTML={{ __html: task.title }}
                        />
                        <Text
                            size="sm"
                            colorToken="text-weaker"
                            dangerouslySetInnerHTML={{ __html: task.description || 'No description' }}
                        />
                    </Sidebar>
                )}
            </View>
        </View>
    )
}
